// adPluga ad tag — IIFE bundle entry point.
//
//   <script async src="https://cdn.adpluga.com/v1/tag.js"
//           data-adpluga-key="pk_live_..." [data-adpluga-base="/v1/"]></script>
//
// The script discovers `[data-adpluga-slot]` elements (now and via
// MutationObserver), fetches `/v1/serve`, renders the creative inside a
// sandboxed iframe, and fires `/v1/track` once the slot becomes viewable.
//
// Public API on `window.AdPluga`:
//   AdPluga.version          – semver of the bundle
//   AdPluga.refresh(target?) – force a re-fetch on a slot (Element or CSS selector)
//   AdPluga.destroy(target?) – tear down a slot (Element or CSS selector)
//   AdPluga.conversion(opts) – fire a conversion event via /v1/track

import {
  DEFAULT_BASE,
  SCRIPT_BASE_ATTR,
  SCRIPT_KEY_ATTR,
  SLOT_ATTR,
  VERSION,
} from "./config";
import { Discoverer } from "./discovery";
import { trackEvent } from "./transport";
import type { ConversionOptions, SelectorOrElement } from "./types";
import { ViewabilityWatcher } from "./viewability";

interface AdPlugaGlobal {
  version: string;
  refresh(target?: SelectorOrElement): void;
  destroy(target?: SelectorOrElement): void;
  conversion(opts: ConversionOptions): void;
}

declare global {
  interface Window {
    AdPluga?: AdPlugaGlobal;
  }
}

(function () {
  if (window.AdPluga) {
    // Double-include guard — common when the same tag is injected by a CMS
    // multiple times. Re-running would double the MutationObservers and
    // burn budget on duplicate impressions.
    return;
  }

  const script = currentScript();
  const key = script?.getAttribute(SCRIPT_KEY_ATTR) ?? "";
  if (!key) {
    console.warn("[adpluga] missing " + SCRIPT_KEY_ATTR + " on <script> tag");
    return;
  }
  const base = normaliseBase(script?.getAttribute(SCRIPT_BASE_ATTR) ?? DEFAULT_BASE);

  const watcher = new ViewabilityWatcher();
  const discoverer = new Discoverer({ key, base, watcher });

  const api: AdPlugaGlobal = {
    version: VERSION,
    refresh(target) {
      forEachTarget(target, (el) => {
        const c = discoverer.controllerFor(el);
        if (c) c.refresh();
      });
    },
    destroy(target) {
      if (target == null) {
        discoverer.stop();
        return;
      }
      forEachTarget(target, (el) => {
        const c = discoverer.controllerFor(el);
        if (c) c.destroy();
      });
    },
    conversion(opts) {
      if (!opts || !opts.token) return;
      trackEvent(base, {
        token: opts.token,
        event: "conversion",
        value_cents: opts.value_cents,
        currency: opts.currency,
        conv_type: opts.conv_type,
      });
    },
  };
  window.AdPluga = api;

  document.addEventListener("visibilitychange", () => {
    const hidden = document.visibilityState === "hidden";
    discoverer.broadcast((c) => c.setPaused(hidden));
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => discoverer.start(), {
      once: true,
    });
  } else {
    discoverer.start();
  }

  function currentScript(): HTMLScriptElement | null {
    if (document.currentScript instanceof HTMLScriptElement) {
      return document.currentScript;
    }
    const scripts = document.getElementsByTagName("script");
    for (let i = scripts.length - 1; i >= 0; i--) {
      const s = scripts[i];
      if (s && s.hasAttribute(SCRIPT_KEY_ATTR)) return s;
    }
    return null;
  }

  function normaliseBase(b: string): string {
    return b.endsWith("/") ? b : b + "/";
  }

  function forEachTarget(
    target: SelectorOrElement | undefined,
    fn: (el: HTMLElement) => void,
  ): void {
    if (target == null) {
      const all = document.querySelectorAll<HTMLElement>("[" + SLOT_ATTR + "]");
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        if (el) fn(el);
      }
      return;
    }
    if (typeof target === "string") {
      const all = document.querySelectorAll<HTMLElement>(target);
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        if (el) fn(el);
      }
      return;
    }
    if (target instanceof HTMLElement) {
      fn(target);
    }
  }
})();
